'use client';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRouter, usePathname } from 'next/navigation';
import { toast } from 'sonner';
import { ApiService } from '@/_services/api.service';
import { queryKeys } from '../queries/queryKeys';

/**
 * Mutation hook to delete a conversation with cache invalidation
 *
 * Features:
 * - Invalidates all conversation pages on delete (total count changes)
 * - Error rollback with toast notification
 * - Redirects to /new if deleting current conversation
 * - Clears messages cache for deleted conversation
 * - Accepts optional onPageAdjust callback for when current page becomes empty
 */
export const useDeleteConversation = (onPageAdjust?: () => void) => {
	const queryClient = useQueryClient();
	const router = useRouter();
	const pathname = usePathname();

	return useMutation({
		mutationFn: async (id: string) => {
			const { error } = await ApiService.DeleteConversation(id);
			if (error) throw new Error(error.message);
		},
		onMutate: async (id) => {
			// Cancel any outgoing refetches for conversations and messages
			await queryClient.cancelQueries({ queryKey: queryKeys.conversations.root });
			await queryClient.cancelQueries({ queryKey: queryKeys.messages.byConversation(id) });
			await queryClient.cancelQueries({ queryKey: queryKeys.conversations.detail(id) });

			// Remove messages and conversation caches entirely to prevent any refetch attempts
			queryClient.removeQueries({ queryKey: queryKeys.messages.byConversation(id) });
			queryClient.removeQueries({ queryKey: queryKeys.conversations.detail(id) });

			// Redirect immediately if we're deleting the current conversation
			// This prevents any further refetch attempts on the deleted conversation
			if (pathname === `/conversation/${id}`) {
				router.push('/new');
			}

			return { deletedId: id };
		},
		onError: (_, id) => {
			// Refetch conversations list to restore the deleted conversation in the sidebar
			queryClient.invalidateQueries({ queryKey: queryKeys.conversations.root });
			queryClient.invalidateQueries({ queryKey: queryKeys.conversations.detail(id) });

			toast.error('Failed to delete conversation');
		},
		onSuccess: () => {
			// Invalidate all conversation pages (total count changed)
			queryClient.invalidateQueries({ queryKey: queryKeys.conversations.root });

			// Call page adjustment callback if provided
			onPageAdjust?.();

			toast.success('Conversation deleted');
		}
	});
};
